import { useCallback, useMemo, useRef } from 'react';
import { FlatList, useWindowDimensions } from 'react-native';
import Animated, { interpolate, useAnimatedStyle } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useRoute } from '@react-navigation/native';

import { Box } from '@/components/lib/components';
import { useFullScreenPagerTransition } from '@/feature/circular-pager/FullScreenPagerTransitionProvider';
import { useScaleCarouselItemMountAnimation } from '@/feature/circular-pager/hooks/use-scale-carousel-item-mount-animation';
import { useStartAutoScroll } from '@/feature/circular-pager/hooks/use-start-auto-scroll';
import { Event } from '@/feature/event-details-sbk/types';
import {
    AUTO_SCROLL_LIST_ENABLED,
    AutoScrollingListLoop,
    AutoScrollingListLoopRef,
} from '@/feature/fantasy-event-sheets/auto-scrolling-list';
import { EVENT_CAROUSEL_HEIGHT } from '@/feature/fantasy-event-sheets/components/EventsCarouselTabBar';
import { styles } from '@/feature/fantasy-event-sheets/components/EventsCarouselTabBar';
import { SbkEventCard } from '@/feature/league-hub-sbk/components/SbkEventCard';
import { useEvents } from '@/feature/league-hub-sbk/hooks/use-events';
import { useEventsTabsStore } from '@/hooks/use-events-tabs-store';
import { common } from '@/styles/styles';
import { MAX_WEB_WIDTH, isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';

import { SbkEventsSheetsParamsList } from '../SbkEventSheets';

const AUTO_SCROLL_LIST_SPACING = 12;

export const SbkEventsCarouselTabBar = () => {
    const { animatedIndex, sheetMountAnimationProgress, isSheetMountCompleted } = useFullScreenPagerTransition();

    const mountingStyles = useAnimatedStyle(() => {
        return {
            opacity: interpolate(sheetMountAnimationProgress.value, [-1, -0.5, 0], [0, 1, 1]),
        };
    });

    const bottomSheetGestureStyles = useAnimatedStyle(() => {
        if (!isSheetMountCompleted.value) {
            return {};
        }

        return {
            opacity: interpolate(animatedIndex.value, [-1, 0, 1], [0, 1, 0]),
            zIndex: animatedIndex.value > 0 ? -1 : 1,
        };
    });

    const position = isWeb ? undefined : common.absolute;

    return (
        <Animated.View style={[position, mountingStyles, bottomSheetGestureStyles]}>
            <EventCarouselTabBarContents />
        </Animated.View>
    );
};

export const EventCarouselTabBarContents = () => {
    const { params } = useRoute<SbkEventsSheetsParamsList['route']>();

    const { data: events } = useEvents({ leagueId: params.leagueId });

    const insets = useSafeAreaInsets();
    const { width } = useWindowDimensions();

    const autoScrollingListRef = useRef<AutoScrollingListLoopRef>(null);

    useStartAutoScroll(autoScrollingListRef, AUTO_SCROLL_LIST_ENABLED);

    // we need this only for the initial render of the carousel
    // so we use useMemo with an empty array to avoid re-computing it on every render
    const initialIndex = useMemo(() => useEventsTabsStore.getState().selectedTab, []);

    return (
        <Box
            style={{ marginTop: insets.top }}
            paddingTop={'s12'}
            zIndex={1}
            height={EVENT_CAROUSEL_HEIGHT}
            width={width}
            maxWidth={isWeb ? MAX_WEB_WIDTH : width}
        >
            {AUTO_SCROLL_LIST_ENABLED ? (
                <AutoScrollingListLoop
                    ref={autoScrollingListRef}
                    style={common.justifyCenter}
                    spacing={AUTO_SCROLL_LIST_SPACING}
                    frameRate={60}
                    speed={0.2}
                    data={events || []}
                    initialIndex={initialIndex}
                    renderItem={({ item, index }) => (
                        <EventCarouselItem event={item} index={index % (events?.length || 0)} />
                    )}
                />
            ) : (
                <FlatList
                    contentContainerStyle={styles.list}
                    data={events}
                    horizontal
                    keyExtractor={item => item.id}
                    renderItem={({ item, index }) => (
                        <EventCarouselItem event={item} index={index % (events?.length || 0)} />
                    )}
                    showsHorizontalScrollIndicator={showScrollIndicator}
                />
            )}
        </Box>
    );
};

const EventCarouselItem = ({ event, index }: { event: Event; index: number }) => {
    // ! the `isSelected` logic *needs* to be inside the "Item" component to avoid flickers
    const isSelected = useEventsTabsStore(state => state.selectedTab === index);
    const setSelectedTab = useEventsTabsStore(state => state.actions.setSelectedTab);

    const navigateToEvent = useCallback(() => {
        setSelectedTab(index);
    }, [index, setSelectedTab]);

    const animatedStyles = useScaleCarouselItemMountAnimation();

    return (
        <Animated.View style={animatedStyles}>
            <SbkEventCard
                event={event}
                isSelected={isSelected}
                navigateToEvent={navigateToEvent}
                variant="eventDetails"
            />
        </Animated.View>
    );
};
